import { DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { CustomHeaderTooltip } from '@components/Admin/Location/Cards/components/CustomHeaderTooltips'
import { simpleNumberDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { CustomSelectEditor } from '@components/_SiteWide/Grid/CellEditors/CustomSelectEditor'
import { NumberCellEditor } from '@components/_SiteWide/Grid/CellEditors/NumberCellEditor'
import { GraviButton, Horizontal, Texto, addCommasToNumber } from '@gravitate-js/excalibrr'
import { Popconfirm, Tooltip } from 'antd'

const cellClassRules = (selectedRows, selectedProperty, isBulkChangeVisible?: boolean) => {
  if (!isBulkChangeVisible || !selectedProperty) {
    return {
      selectedEvenRow: () => false,
      selectedOddRow: () => false,
      selectedColumn: () => false,
      selectedEdit: () => false,
    }
  }
  return {
    selectedColumn: (params) => {
      const isProperty = selectedProperty === params.colDef.field
      const isSelected = selectedRows?.some((row) => row?.id === params.data?.id)
      return isProperty && !isSelected
    },
    selectedEdit: (params) => {
      const isProperty = selectedProperty === params.colDef.field
      const isSelected = selectedRows?.some((row) => row?.id === params.data?.id)
      return isProperty && isSelected
    },
    selectedEvenRow: (params) => {
      const field = params.colDef.field === 'name' ? 'card_name' : params.colDef.field

      const isProperty = selectedProperty === field
      const isDispatchImpact = field === 'dispatch_impact'
      const isSelected = selectedRows?.some((row) => row?.id === params.data?.id)
      const isEven = params.node.rowIndex % 2 === 0
      return isEven && isSelected && !isProperty && !isDispatchImpact
    },
    selectedOddRow: (params) => {
      const field = params.colDef.field === 'name' ? 'card_name' : params.colDef.field
      const isDispatchImpact = field === 'dispatch_impact'
      const isProperty = selectedProperty === field
      const isSelected = selectedRows?.some((row) => row?.id === params.data?.id)
      const isEven = params.node.rowIndex % 2 === 0
      return !isEven && isSelected && !isProperty && !isDispatchImpact
    },
  }
}

const headerClass = (selectedProperty, isBulkChangeVisible, params) => {
  if (!isBulkChangeVisible) {
    return null
  }
  const isProperty = selectedProperty === params.colDef.field
  if (isProperty && isBulkChangeVisible) {
    return 'selectedHeader'
  }
}

export const getColumnDefs = (handleDelete, selectedRows, selectedProperty, isBulkChangeVisible, canEdit) => {
  const result = [
    {
      headerName: 'Terminal Name',
      field: 'terminal',
      tooltipComponent: CustomHeaderTooltip,
      headerTooltip: true,
      editable: false,
      cellClassRules: cellClassRules(selectedRows, selectedProperty, isBulkChangeVisible),
    },
    {
      headerName: 'Card Name',
      field: 'name',
      tooltipComponent: CustomHeaderTooltip,
      headerTooltip: true,
      editable: true,
      cellEditor: 'agTextCellEditor',
      cellEditorParams: {
        maxLength: 60,
      },
      cellClassRules: cellClassRules(selectedRows, selectedProperty, isBulkChangeVisible),
      headerClass: (params) => headerClass(selectedProperty, isBulkChangeVisible, params),
      comparator: (valueA, valueB) => (valueA || '')?.toLowerCase().localeCompare((valueB || '')?.toLowerCase()),
    },
    {
      headerName: 'Activity',
      field: 'activity',
      tooltipComponent: CustomHeaderTooltip,
      headerTooltip: true,
      editable: true,
      minWidth: 110,
      cellEditor: CustomSelectEditor,
      cellEditorParams: {
        width: 90,
        hideSearch: true,
        options: ['Loading', 'Unloading'].map((option) => ({
          value: option,
          label: option,
        })),
      },
      cellClassRules: cellClassRules(selectedRows, selectedProperty, isBulkChangeVisible),
      headerClass: (params) => headerClass(selectedProperty, isBulkChangeVisible, params),
    },
    {
      headerName: 'Certification Trigger',
      field: 'certification_trigger',
      tooltipComponent: CustomHeaderTooltip,
      headerTooltip: true,
      editable: true,
      minWidth: 200,
      cellEditor: CustomSelectEditor,
      cellEditorParams: {
        width: 180,
        hideSearch: true,
        options: ['Automatic - Activity Based', 'Manual - Date Based', 'Never Expires'].map((option) => ({
          value: option,
          label: option,
        })),
      },
      cellClassRules: cellClassRules(selectedRows, selectedProperty, isBulkChangeVisible),
      headerClass: (params) => headerClass(selectedProperty, isBulkChangeVisible, params),
    },
    {
      headerName: 'Activity Threshold',
      field: 'activity_threshold',
      ...simpleNumberDef('activity_threshold', 0),
      tooltipComponent: CustomHeaderTooltip,
      headerTooltip: true,
      editable: true,
      cellClassRules: cellClassRules(selectedRows, selectedProperty, isBulkChangeVisible),
      headerClass: (params) => headerClass(selectedProperty, isBulkChangeVisible, params),
      cellRenderer: (params) => {
        if (params.data.activity_threshold !== null) {
          const isLight = params.data.certification_trigger === 'Never Expires'
          return (
            <Texto style={{ color: isLight ? 'var(--gray-400)' : 'inherit' }} align='right'>
              {addCommasToNumber(params.data.activity_threshold, 0)}
            </Texto>
          )
        }
      },
    },
    {
      headerName: 'Days Valid',
      field: 'days_valid',
      tooltipComponent: CustomHeaderTooltip,
      headerTooltip: true,
      editable: true,
      ...simpleNumberDef('days_valid', 0),
      cellEditor: NumberCellEditor,
      cellEditorParams: {
        minValue: (params) => params.data.warning_threshold || 0,
      },
      cellClassRules: cellClassRules(selectedRows, selectedProperty, isBulkChangeVisible),
      headerClass: (params) => headerClass(selectedProperty, isBulkChangeVisible, params),
      cellRenderer: (params) => {
        if (params.data.days_valid !== null) {
          const isLight = params.data.certification_trigger === 'Never Expires'
          return (
            <Texto style={{ color: isLight ? 'var(--gray-400)' : 'inherit' }} align='right'>
              {addCommasToNumber(params.data.days_valid, 0)}
            </Texto>
          )
        }
      },
    },
    {
      headerName: 'Warning Threshold',
      field: 'warning_threshold',
      tooltipComponent: CustomHeaderTooltip,
      headerTooltip: true,
      ...simpleNumberDef('warning_threshold', 0),
      editable: true,
      cellEditor: NumberCellEditor,
      cellEditorParams: {
        maxValue: (params) => params.data.days_valid || 10000,
      },
      comparator: (_a, _b, nodeA, nodeB) => nodeA.data?.warning_threshold - nodeB.data?.warning_threshold,
      cellClassRules: cellClassRules(selectedRows, selectedProperty, isBulkChangeVisible),
      headerClass: (params) => headerClass(selectedProperty, isBulkChangeVisible, params),
      cellRenderer: (params) => {
        if (params.data.warning_threshold !== null) {
          const isLight = params.data.certification_trigger === 'Never Expires'
          return (
            <Texto style={{ color: isLight ? 'var(--gray-400)' : 'inherit' }} align='right'>
              {addCommasToNumber(params.data.warning_threshold, 0)}
            </Texto>
          )
        }
      },
    },
    {
      headerName: 'Dispatch Impact',
      field: 'dispatch_impact',
      minWidth: 210,
      tooltipComponent: CustomHeaderTooltip,
      tooltipComponentParams: { width: `600px` },
      headerTooltip: true,
      cellStyle: (params) => {
        const dispatchImpact = params.data?.dispatch_impact
        const bgColor =
          dispatchImpact === 'Block Dispatch'
            ? 'var(--theme-error-dim)'
            : dispatchImpact === 'Informational (No Impact)'
              ? 'var(--theme-color-2-dim)'
              : 'var(--theme-warning-dim)'
        const color =
          dispatchImpact === 'Block Dispatch'
            ? 'var(--theme-error)'
            : dispatchImpact === 'Informational (No Impact)'
              ? 'var(--theme-color-2)'
              : 'var(--theme-warning)'
        return { backgroundColor: bgColor, color, fontWeight: 600 }
      },
      editable: true,
      cellEditor: CustomSelectEditor,
      cellEditorParams: {
        width: 190,
        setupFunc: (setValue, property) => {
          if (property === 'Never Expires') {
            setValue('Informational (No Impact)')
          }
        },
        linkedField: 'certification_trigger',
        options: [
          {
            value: 'Alert',
            label: (
              <Horizontal style={{ backgroundColor: 'var(--theme-warning-dim)' }}>
                <Texto>Alert</Texto>
              </Horizontal>
            ),
            className: 'warning-dim-bg ',
          },
          {
            value: 'Alert Manual, Block Optimizer',
            label: (
              <Horizontal style={{ backgroundColor: 'var(--theme-warning-dim)' }}>
                <Texto>Alert Manual, Block Optimizer</Texto>
              </Horizontal>
            ),
            className: 'warning-dim-bg',
          },
          {
            value: 'Block Dispatch',
            label: (
              <Horizontal style={{ backgroundColor: 'var(--theme-error-dim)' }}>
                <Texto>Block Dispatch</Texto>
              </Horizontal>
            ),
            className: 'error-dim-bg',
          },
          {
            value: 'Informational (No Impact)',
            label: (
              <Horizontal style={{ backgroundColor: 'var(--theme-color-2-dim)' }} className='full-height-width'>
                <Texto>Informational (No Impact)</Texto>
              </Horizontal>
            ),
            className: 'theme-2-bg',
          },
        ],
        filterOption: (input, option) => option.value.toLowerCase().includes(input?.toLowerCase()),
        mode: 'single',
        accessor: 'dispatch_impact',
        hideSearch: true,
      },
      cellClassRules: cellClassRules(selectedRows, selectedProperty, isBulkChangeVisible),
      headerClass: (params) => headerClass(selectedProperty, isBulkChangeVisible, params),
    },
    {
      headerName: 'Carding Instructions',
      field: 'carding_instructions',
      editable: true,
      cellEditor: 'agTextCellEditor',
      cellEditorParams: {
        maxLength: 300,
      },
      tooltipComponent: CustomHeaderTooltip,
      headerTooltip: true,
      cellRenderer: (params) => {
        return (
          <Tooltip title={params.data?.carding_instructions}>
            <GraviButton
              className='ghost-gravi-button'
              // @ts-ignore - TODO: GraviButton needs to accept ReactNode as it's type for buttonText
              buttonText={
                <Texto className='text-truncate' weight='normal'>
                  {params.data?.carding_instructions}
                </Texto>
              }
            />
          </Tooltip>
        )
      },
      cellClassRules: cellClassRules(selectedRows, selectedProperty, isBulkChangeVisible),
      headerClass: (params) => headerClass(selectedProperty, isBulkChangeVisible, params),
    },
    {
      headerName: 'Supply Zone',
      field: 'supply_zone',
      initialHide: true,
      editable: false,
    },
    {
      headerName: 'Market',
      field: 'market',
      initialHide: true,
      editable: false,
    },
  ]
  if (canEdit) {
    result.push({
      maxWidth: 100,
      headerName: 'Actions',
      sortable: false,
      filter: false,
      editable: false,
      field: 'terminal-card-actions',
      cellRenderer: (params) => {
        if (isBulkChangeVisible) {
          return null
        }
        return (
          <Horizontal verticalCenter justifyContent='end' style={{ overflow: 'visible' }}>
            <Popconfirm
              title='Deletes this card from this terminal and from drivers with this card at this terminal.  Continue?'
              onConfirm={() => handleDelete({ terminal_id: params.data?.terminal_id, card_id: params.data?.id })}
              icon={<QuestionCircleOutlined style={{ color: 'var(--theme-error)' }} />}
              okText='Confirm'
              placement='topRight'
            >
              <GraviButton style={{ maxHeight: 32 }} icon={<DeleteOutlined />} error appearance='solid' />
            </Popconfirm>
          </Horizontal>
        )
      },
      tooltipComponent: CustomHeaderTooltip,
      headerTooltip: true,
      tooltipComponentParams: { width: `400px` },
      cellClassRules: cellClassRules(selectedRows, selectedProperty, isBulkChangeVisible),
    })
  }
  return result
}
